/**
 * Countdown Timer Web Component
 *
 * Displays a countdown to a specified end date/time.
 * Automatically hides when timer reaches zero.
 *
 * Usage:
 * <countdown-timer data-end-date="2026-12-31T23:59:00">
 *   ... timer markup ...
 * </countdown-timer>
 *
 * Optional:
 * - data-hours-as-total: when present, [data-unit="hours"] renders the TOTAL
 *   remaining hours (days * 24 + hours) and the days unit is omitted, e.g.
 *   "29 : 34 : 04". Used by the cart free-gift banner (HH : MM : SS).
 */

class CountdownTimer extends HTMLElement {
  connectedCallback() {
    this.endDate = this._resolveEndDate();
    if (!this.endDate) return;

    this.updateTimer();
    this.interval = setInterval(() => this.updateTimer(), 1000);
    requestAnimationFrame(() => this._initMarquee());
  }

  _resolveEndDate() {
    const duration = parseInt(this.dataset.timerDuration, 10);
    if (duration > 0) {
      // ponytail: localStorage key scoped to first class so multiple timers don't collide
      const key = `countdown-end:${this.classList[0] || 'default'}`;
      let end = parseInt(localStorage.getItem(key), 10);
      if (!end || end <= Date.now()) {
        end = Date.now() + duration * 60 * 1000;
        localStorage.setItem(key, String(end));
      }
      return new Date(end);
    }

    const str = this.dataset.endDate;
    if (!str) {
      console.warn('CountdownTimer: no end date or duration provided');
      return null;
    }
    const d = new Date(str);
    if (isNaN(d.getTime())) {
      console.warn('CountdownTimer: invalid date format:', str);
      return null;
    }
    return d;
  }

  updateTimer() {
    const now = new Date();
    const diff = this.endDate - now;

    // Timer expired
    if (diff <= 0) {
      this.style.display = 'none';
      this.clearTimer();
      return;
    }

    // Calculate time units
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    // When hours should absorb the days (HH : MM : SS format), roll days into hours.
    const hoursAsTotal = this.hasAttribute('data-hours-as-total');
    const displayHours = hoursAsTotal ? days * 24 + hours : hours;

    // querySelectorAll keeps clone digits in sync with the originals
    this.querySelectorAll('[data-unit="days"]').forEach(el => { el.textContent = String(days).padStart(2, '0'); });
    this.querySelectorAll('[data-unit="hours"]').forEach(el => { el.textContent = String(displayHours).padStart(2, '0'); });
    this.querySelectorAll('[data-unit="minutes"]').forEach(el => { el.textContent = String(minutes).padStart(2, '0'); });
    this.querySelectorAll('[data-unit="seconds"]').forEach(el => { el.textContent = String(seconds).padStart(2, '0'); });
  }

  _initMarquee() {
    const inner = this.querySelector('.cart-timer__inner');
    if (!inner || inner.scrollWidth <= inner.clientWidth) return;

    const track = document.createElement('div');
    track.className = 'cart-timer__track';
    while (inner.firstChild) track.appendChild(inner.firstChild);

    const clone = track.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');

    const wrapper = document.createElement('div');
    wrapper.className = 'cart-timer__marquee-wrapper';
    wrapper.appendChild(track);
    wrapper.appendChild(clone);

    inner.appendChild(wrapper);
    inner.classList.add('cart-timer__inner--marquee');
  }

  clearTimer() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  disconnectedCallback() {
    this.clearTimer();
  }
}

// Register the custom element
if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
